/**
 * MultiRoleSlotsEditor — Iter-297 / Iter-309 — Additional staffing roles per event.
 *
 * Lets the admin or client attach extra role slots to a single event
 * without leaving the Create Job page. The primary form already
 * captures the first role + shared event metadata; this widget renders
 * a card per "additional" slot below it.
 *
 * Each slot owns: role, quantity, bill_rate, talent_pay_rate, optional
 * start/end time override, dress code, additional notes, certifications,
 * + per-role backup config + per-day overrides (multi-day events only).
 *
 * Iter-309 additions:
 *   • `isMultiDay` + `eventStartDate` + `eventEndDate` props let the
 *     editor know to render a per-day section per role.
 *   • Each slot now carries `per_day_overrides`: a sparse list keyed by
 *     date. Days not in the list inherit the slot's primary values
 *     (which themselves inherit the parent event's times by default).
 *   • Per-day editing UI is collapsed by default ("Same setup all 3 days
 *     — Customize per day"). Expanding it reveals one row per day with
 *     editable: start/end time, headcount, bill rate, talent pay rate,
 *     venue address.
 *   • Each role gets an optional `backup` toggle that mirrors the
 *     parent form's backup-staffing controls (backups_needed,
 *     backup_pay_type, backup_flat_pay, backup_hourly_rate). When
 *     enabled, the parent submit payload includes those values per
 *     additional role so the backend can create role-specific standby
 *     bookings.
 */
import React, { useMemo } from 'react';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Plus, Trash2, Users as UsersIcon, DollarSign, Shirt, ClipboardList,
  Clock, ShieldCheck, ChevronDown, ChevronUp, Calendar, MapPin, LifeBuoy,
} from 'lucide-react';

export function emptyExtraSlot() {
  return {
    role_id: '',
    talent_needed: 1,
    bill_rate: '',
    talent_pay_rate: '',
    start_time: '',
    end_time: '',
    package_tier: '',
    dress_code: '',
    additional_notes: '',
    certifications: '',  // comma-separated string; split on submit
    // Iter-309 — Per-role backup config
    backup_enabled: false,
    backups_needed: 0,
    backup_pay_type: 'flat',
    backup_flat_pay: '',
    backup_hourly_rate: '',
    // Iter-309 — Per-day overrides (multi-day events). Keyed by ISO date.
    // Each entry: { date, start_time, end_time, talent_needed, bill_rate,
    //               talent_pay_rate, venue_address, enabled }
    // The UI maintains the FULL list (one entry per event day) but only
    // sets `enabled=true` when the user actually edited a row. Submit-time
    // the parent serializer drops disabled rows.
    per_day_overrides: [],
    customize_per_day: false,
  };
}

// Helper — build the list of dates between `start` and `end` (inclusive).
// Returns ISO strings in chronological order. Empty if either is falsy
// or the range is invalid.
function _datesBetween(startStr, endStr) {
  if (!startStr) return [];
  const start = new Date(`${startStr}T00:00:00`);
  const end = endStr ? new Date(`${endStr}T00:00:00`) : start;
  if (isNaN(start) || isNaN(end) || end < start) return [];
  const out = [];
  const cur = new Date(start);
  let safety = 0;
  while (cur <= end && safety++ < 366) {
    out.push(cur.toISOString().slice(0, 10));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

export default function MultiRoleSlotsEditor({
  slots,
  onChange,
  roles,
  primaryRoleId,
  primaryStartTime,
  primaryEndTime,
  isMultiDay = false,
  eventStartDate = '',
  eventEndDate = '',
}) {
  const update = (idx, patch) => {
    onChange(slots.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const remove = (idx) => {
    onChange(slots.filter((_, i) => i !== idx));
  };

  const add = () => {
    onChange([...slots, emptyExtraSlot()]);
  };

  // Iter-309 — the canonical event-day list. Per-role per-day rows are
  // re-seeded from this whenever it changes (the parent useEffect on
  // shift_date/end_date already triggers the appropriate re-render).
  const eventDays = useMemo(
    () => (isMultiDay ? _datesBetween(eventStartDate, eventEndDate) : []),
    [isMultiDay, eventStartDate, eventEndDate],
  );

  // For a given slot index, ensure per_day_overrides has one entry per
  // event day. Missing entries are appended with `enabled=false`
  // (i.e. inherit the slot's primary values). Extra entries (e.g. user
  // shortened the date range) are dropped silently.
  const ensurePerDayRows = (idx, slot) => {
    if (!isMultiDay || eventDays.length === 0) return;
    const byDate = new Map((slot.per_day_overrides || []).map((r) => [r.date, r]));
    const next = eventDays.map((d) => byDate.get(d) || {
      date: d,
      enabled: false,
      start_time: '',
      end_time: '',
      talent_needed: '',
      bill_rate: '',
      talent_pay_rate: '',
      venue_address: '',
    });
    // Avoid no-op re-render: only set state when the canonical list differs
    if (JSON.stringify(next) !== JSON.stringify(slot.per_day_overrides || [])) {
      update(idx, { per_day_overrides: next });
    }
  };

  // Hide roles already used by the primary slot or other extras so the
  // admin can't accidentally double-book the same role-slot under the
  // event. The dedupe unique-index would reject it server-side anyway,
  // but a friendlier UX hides the option.
  const usedRoleIds = new Set([primaryRoleId, ...slots.map((s) => s.role_id)].filter(Boolean));

  return (
    <Card className="border-dashed border-2 border-cyan-200 bg-cyan-50/40">
      <CardContent className="pt-5 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <UsersIcon className="w-4 h-4 text-cyan-600" />
              Additional Staffing Roles
            </h3>
            <p className="text-xs text-gray-600 mt-0.5 max-w-xl">
              Add other roles needed for the same event (e.g. 2 Catering + 1
              Bartender + 3 Brand Ambassadors). Each row creates a separate
              shift under the same event, with its own quantity, rates, and
              optional time / dress overrides.
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={add}
            className="text-cyan-700 border-cyan-300 hover:bg-cyan-100 shrink-0"
            data-testid="add-role-slot-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            Add Role
          </Button>
        </div>

        {slots.length === 0 ? (
          <div className="text-xs text-gray-500 italic text-center py-3">
            No additional roles yet — this event will have just the primary role above.
          </div>
        ) : (
          slots.map((slot, idx) => (
            <Card
              key={idx}
              className="border-cyan-200 bg-white"
              data-testid={`role-slot-${idx}`}
            >
              <CardContent className="pt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-cyan-700">
                    Role Slot #{idx + 2}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => remove(idx)}
                    className="text-rose-600 hover:bg-rose-50 h-8 px-2"
                    data-testid={`remove-role-slot-${idx}`}
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" />
                    Remove
                  </Button>
                </div>

                {/* Role + Quantity */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-2 space-y-1.5">
                    <Label className="text-xs font-semibold">Role *</Label>
                    <Select
                      value={slot.role_id || ''}
                      onValueChange={(v) => update(idx, { role_id: v })}
                    >
                      <SelectTrigger
                        className="bg-white"
                        data-testid={`role-slot-${idx}-role-trigger`}
                      >
                        <SelectValue placeholder="Pick a role" />
                      </SelectTrigger>
                      <SelectContent>
                        {roles
                          .filter((r) => slot.role_id === r.id || !usedRoleIds.has(r.id))
                          .map((r) => (
                            <SelectItem key={r.id} value={r.id}>
                              {r.name}
                              <span className="ml-2 text-[10px] uppercase tracking-wide text-gray-400">
                                {r.tier_type}
                              </span>
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold flex items-center gap-1">
                      <UsersIcon className="w-3 h-3" />
                      Quantity *
                    </Label>
                    <Input
                      type="number"
                      min="1"
                      max="50"
                      value={slot.talent_needed}
                      onChange={(e) => update(idx, { talent_needed: parseInt(e.target.value || '1', 10) })}
                      data-testid={`role-slot-${idx}-qty`}
                    />
                  </div>
                </div>

                {/* Rates */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold flex items-center gap-1">
                      <DollarSign className="w-3 h-3" />
                      Bill Rate *
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={slot.bill_rate}
                      onChange={(e) => update(idx, { bill_rate: e.target.value })}
                      placeholder="0.00"
                      data-testid={`role-slot-${idx}-bill`}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold flex items-center gap-1">
                      <DollarSign className="w-3 h-3" />
                      Talent Pay Rate
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={slot.talent_pay_rate}
                      onChange={(e) => update(idx, { talent_pay_rate: e.target.value })}
                      placeholder="Auto"
                      data-testid={`role-slot-${idx}-pay`}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold">Package Tier</Label>
                    <Select
                      value={slot.package_tier || 'inherit'}
                      onValueChange={(v) => update(idx, { package_tier: v === 'inherit' ? '' : v })}
                    >
                      <SelectTrigger className="bg-white" data-testid={`role-slot-${idx}-tier`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="inherit">Inherit from event</SelectItem>
                        <SelectItem value="labor">Labor</SelectItem>
                        <SelectItem value="hospitality">Hospitality</SelectItem>
                        <SelectItem value="premium_activation">Premium Activation</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Time overrides */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Start Time
                      <span className="font-normal text-gray-400 ml-1">
                        (default {primaryStartTime || '—'})
                      </span>
                    </Label>
                    <Input
                      type="time"
                      value={slot.start_time}
                      onChange={(e) => update(idx, { start_time: e.target.value })}
                      data-testid={`role-slot-${idx}-start`}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      End Time
                      <span className="font-normal text-gray-400 ml-1">
                        (default {primaryEndTime || '—'})
                      </span>
                    </Label>
                    <Input
                      type="time"
                      value={slot.end_time}
                      onChange={(e) => update(idx, { end_time: e.target.value })}
                      data-testid={`role-slot-${idx}-end`}
                    />
                  </div>
                </div>

                {/* Dress code + certifications */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold flex items-center gap-1">
                      <Shirt className="w-3 h-3" />
                      Dress Code Override
                    </Label>
                    <Input
                      value={slot.dress_code}
                      onChange={(e) => update(idx, { dress_code: e.target.value })}
                      placeholder="e.g. All Black"
                      maxLength={120}
                      data-testid={`role-slot-${idx}-dress`}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold flex items-center gap-1">
                      <ShieldCheck className="w-3 h-3" />
                      Required Certifications
                    </Label>
                    <Input
                      value={slot.certifications}
                      onChange={(e) => update(idx, { certifications: e.target.value })}
                      placeholder="TIPS, ABC, ServSafe"
                      maxLength={200}
                      data-testid={`role-slot-${idx}-certs`}
                    />
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold flex items-center gap-1">
                    <ClipboardList className="w-3 h-3" />
                    Notes
                  </Label>
                  <Textarea
                    value={slot.additional_notes}
                    onChange={(e) => update(idx, { additional_notes: e.target.value })}
                    placeholder="Setup instructions, must-have skills…"
                    rows={2}
                    maxLength={800}
                    data-testid={`role-slot-${idx}-notes`}
                  />
                </div>

                {/* ── Iter-309 — Per-role Backup Staffing toggle ─────── */}
                <div className="rounded-md border border-amber-200 bg-amber-50/30 p-3" data-testid={`role-slot-${idx}-backup-block`}>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!slot.backup_enabled}
                      onChange={(e) => update(idx, { backup_enabled: e.target.checked })}
                      data-testid={`role-slot-${idx}-backup-toggle`}
                      className="rounded border-amber-400"
                    />
                    <LifeBuoy className="w-3.5 h-3.5 text-amber-600" />
                    <span className="text-xs font-semibold text-gray-800">
                      Add Backup Staffing for this role
                    </span>
                    <span className="text-[10px] font-normal text-gray-500">
                      (standby talent — paid only if activated)
                    </span>
                  </label>
                  {slot.backup_enabled && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs">Backups Needed</Label>
                        <Input
                          type="number" min="0" max="20"
                          value={slot.backups_needed || 0}
                          onChange={(e) => update(idx, { backups_needed: Math.max(0, Math.min(20, parseInt(e.target.value || '0', 10) || 0)) })}
                          className="bg-white"
                          data-testid={`role-slot-${idx}-backup-count`}
                        />
                      </div>
                      {(parseInt(slot.backups_needed) || 0) > 0 && (
                        <>
                          <div className="space-y-1.5">
                            <Label className="text-xs">Pay Type</Label>
                            <Select
                              value={slot.backup_pay_type || 'flat'}
                              onValueChange={(v) => update(idx, { backup_pay_type: v })}
                            >
                              <SelectTrigger className="bg-white" data-testid={`role-slot-${idx}-backup-pay-type`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="flat">Flat Rate</SelectItem>
                                <SelectItem value="hourly">Hourly</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1.5">
                            {slot.backup_pay_type === 'hourly' ? (
                              <>
                                <Label className="text-xs">Backup Hourly Rate ($/hr)</Label>
                                <Input
                                  type="number" step="0.01" min="0"
                                  value={slot.backup_hourly_rate}
                                  onChange={(e) => update(idx, { backup_hourly_rate: e.target.value })}
                                  placeholder="e.g. 20.00"
                                  className="bg-white"
                                  data-testid={`role-slot-${idx}-backup-hourly`}
                                />
                              </>
                            ) : (
                              <>
                                <Label className="text-xs">Backup Flat Pay ($)</Label>
                                <Input
                                  type="number" step="0.01" min="0"
                                  value={slot.backup_flat_pay}
                                  onChange={(e) => update(idx, { backup_flat_pay: e.target.value })}
                                  placeholder="e.g. 50.00"
                                  className="bg-white"
                                  data-testid={`role-slot-${idx}-backup-flat`}
                                />
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* ── Iter-309 — Per-day overrides (multi-day events) ── */}
                {isMultiDay && eventDays.length > 1 && (() => {
                  // Make sure the per-day list is in sync before we render.
                  // Side-effect during render is fine here because
                  // ensurePerDayRows is idempotent + early-returns when
                  // the list is already canonical.
                  ensurePerDayRows(idx, slot);
                  const days = slot.per_day_overrides || [];
                  const customEnabled = !!slot.customize_per_day;
                  return (
                    <div className="rounded-md border border-cyan-200 bg-cyan-50/30 p-3" data-testid={`role-slot-${idx}-per-day-block`}>
                      <button
                        type="button"
                        onClick={() => update(idx, { customize_per_day: !customEnabled })}
                        className="w-full flex items-center justify-between gap-2 text-left"
                        data-testid={`role-slot-${idx}-customize-per-day-toggle`}
                      >
                        <div className="flex items-center gap-2">
                          <Calendar className="w-3.5 h-3.5 text-cyan-700" />
                          <span className="text-xs font-semibold text-gray-800">
                            Same setup for all {eventDays.length} days
                          </span>
                          <span className="text-[10px] font-normal text-gray-500">
                            (uses role&apos;s primary times + headcount + rates above)
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-[11px] font-medium text-cyan-700">
                          {customEnabled ? 'Hide per-day' : 'Customize per day'}
                          {customEnabled ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        </div>
                      </button>

                      {customEnabled && (
                        <div className="mt-3 overflow-x-auto">
                          <table className="w-full text-xs" data-testid={`role-slot-${idx}-per-day-table`}>
                            <thead>
                              <tr className="text-[10px] uppercase tracking-wider text-gray-500">
                                <th className="text-left font-semibold px-1 py-1">Day</th>
                                <th className="text-left font-semibold px-1 py-1">Override</th>
                                <th className="text-left font-semibold px-1 py-1">Start</th>
                                <th className="text-left font-semibold px-1 py-1">End</th>
                                <th className="text-left font-semibold px-1 py-1"># Staff</th>
                                <th className="text-left font-semibold px-1 py-1">Bill $</th>
                                <th className="text-left font-semibold px-1 py-1">Pay $</th>
                                <th className="text-left font-semibold px-1 py-1">Venue Address</th>
                              </tr>
                            </thead>
                            <tbody>
                              {days.map((d, di) => {
                                const upd = (patch) => {
                                  const next = days.map((row, j) => (j === di ? { ...row, ...patch } : row));
                                  update(idx, { per_day_overrides: next });
                                };
                                return (
                                  <tr key={d.date} className={d.enabled ? 'bg-white' : 'bg-transparent'}>
                                    <td className="px-1 py-1 font-medium text-gray-700 whitespace-nowrap">
                                      {new Date(`${d.date}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                    </td>
                                    <td className="px-1 py-1">
                                      <input
                                        type="checkbox"
                                        checked={!!d.enabled}
                                        onChange={(e) => upd({ enabled: e.target.checked })}
                                        data-testid={`role-slot-${idx}-day-${di}-enable`}
                                      />
                                    </td>
                                    <td className="px-1 py-1">
                                      <Input type="time" value={d.start_time}
                                        onChange={(e) => upd({ start_time: e.target.value, enabled: true })}
                                        className="h-7 text-xs"
                                        data-testid={`role-slot-${idx}-day-${di}-start`} />
                                    </td>
                                    <td className="px-1 py-1">
                                      <Input type="time" value={d.end_time}
                                        onChange={(e) => upd({ end_time: e.target.value, enabled: true })}
                                        className="h-7 text-xs"
                                        data-testid={`role-slot-${idx}-day-${di}-end`} />
                                    </td>
                                    <td className="px-1 py-1">
                                      <Input type="number" min="0" max="50" value={d.talent_needed}
                                        onChange={(e) => upd({ talent_needed: e.target.value, enabled: true })}
                                        placeholder={slot.talent_needed}
                                        className="h-7 text-xs w-16"
                                        data-testid={`role-slot-${idx}-day-${di}-qty`} />
                                    </td>
                                    <td className="px-1 py-1">
                                      <Input type="number" step="0.01" min="0" value={d.bill_rate}
                                        onChange={(e) => upd({ bill_rate: e.target.value, enabled: true })}
                                        placeholder={slot.bill_rate || '0'}
                                        className="h-7 text-xs w-20"
                                        data-testid={`role-slot-${idx}-day-${di}-bill`} />
                                    </td>
                                    <td className="px-1 py-1">
                                      <Input type="number" step="0.01" min="0" value={d.talent_pay_rate}
                                        onChange={(e) => upd({ talent_pay_rate: e.target.value, enabled: true })}
                                        placeholder={slot.talent_pay_rate || 'Auto'}
                                        className="h-7 text-xs w-20"
                                        data-testid={`role-slot-${idx}-day-${di}-pay`} />
                                    </td>
                                    <td className="px-1 py-1">
                                      <div className="flex items-center gap-1">
                                        <MapPin className="w-3 h-3 text-gray-400 shrink-0" />
                                        <Input value={d.venue_address}
                                          onChange={(e) => upd({ venue_address: e.target.value, enabled: true })}
                                          placeholder="Inherit"
                                          className="h-7 text-xs"
                                          data-testid={`role-slot-${idx}-day-${di}-venue`} />
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                          <p className="text-[10px] text-gray-500 mt-2 italic">
                            Days without an override checkbox use this role&apos;s primary times + headcount + rates above.
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          ))
        )}

        {slots.length > 0 && (
          <div className="text-[11px] text-gray-500 italic pt-1">
            Submitting will create {slots.length + 1} linked shift{slots.length === 0 ? '' : 's'}
            {' '}under one event group.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
